import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Users, Search, Download, RefreshCw, AlertTriangle,
  CheckCircle, XCircle, Clock, CreditCard, ChevronDown
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FinancialTabNav from '../components/FinancialTabNav';
import api from '../services/api';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'green', icon: CheckCircle },
  trialing: { label: 'Trial', color: 'amber', icon: Clock },
  past_due: { label: 'Past Due', color: 'red', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'gray', icon: XCircle },
  none: { label: 'No Plan', color: 'gray', icon: XCircle },
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const AdminFinancialSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [paidFilter, setPaidFilter] = useState('');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (paidFilter) params.append('is_paid', paidFilter);
      if (search.trim()) params.append('search', search.trim());
      params.append('limit', '100');

      const res = await api.get(`/admin/financial/subscribers?${params.toString()}`);
      setSubscribers(res.data.subscribers || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, paidFilter, search]);

  useEffect(() => {
    const timer = setTimeout(fetchSubscribers, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchSubscribers]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await api.get(`/admin/financial/subscribers/export${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.none;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-${config.color}-50 text-${config.color}-700`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  return (
    <Sidebar>
      <FinancialTabNav />
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-4">

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by company name or subdomain..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="trialing">Trialing</option>
                  <option value="past_due">Past Due</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="no_subscription">No Plan</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Paid Filter */}
              <div className="relative">
                <select
                  value={paidFilter}
                  onChange={(e) => setPaidFilter(e.target.value)}
                  className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Billing</option>
                  <option value="true">Paid (Valid CC)</option>
                  <option value="false">Unpaid</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Export */}
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>

              <span className="text-sm text-gray-400">{total} companies</span>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : subscribers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Users className="w-8 h-8 mb-2" />
                <p className="text-sm">No subscribers found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Company</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Plan</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Status</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Paid</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500">MRR</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500">Overages</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500">Total/mo</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-500">Users</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-500">Customers</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">CC</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Since</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {subscribers.map(sub => (
                      <tr key={sub.id} className="hover:bg-gray-50/50 transition">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{sub.name || '—'}</p>
                            {sub.subdomain && <p className="text-xs text-gray-400">{sub.subdomain}.trados.app</p>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{sub.plan_name || '—'}</td>
                        <td className="px-4 py-3"><StatusBadge status={sub.subscription_status || 'none'} /></td>
                        <td className="px-4 py-3">
                          {sub.is_paid ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-gray-300" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">${sub.mrr}</td>
                        <td className="px-4 py-3 text-right">
                          {sub.overage_amount > 0 ? (
                            <span className="text-amber-600 font-medium">${sub.overage_amount}</span>
                          ) : (
                            <span className="text-gray-300">$0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">${sub.total_monthly}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={sub.overage_users > 0 ? 'text-amber-600 font-medium' : 'text-gray-600'}>
                            {sub.admin_count}/{sub.user_limit || '∞'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={sub.overage_customers > 0 ? 'text-amber-600 font-medium' : 'text-gray-600'}>
                            {sub.customer_count}/{sub.customer_limit || '∞'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {sub.cc_last4 ? (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                              <CreditCard className="w-3 h-3" />
                              •••• {sub.cc_last4}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400">{formatDate(sub.subscription_started_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </Sidebar>
  );
};

export default AdminFinancialSubscribers;
